import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { SearchBox } from '@fluentui/react'
import { List, SearchRequest, SearchResponse } from '@microsoft/microsoft-graph-types'
import { DataPromise, Destination, GraphError, IGraphArrayPayload } from '../../../context/AxiosContext'
import useEntity from '../../../hooks/useEntity'
import { IRootOverviewItemDto } from '../../../config/documentConfig'
import useSharePoint from '../../../hooks/useSharePoint'
import { IFileDto } from '../../../config/fileConfig'
import { useTranslation } from 'react-i18next'

type Props = {
    file: IFileDto
    rootFolder: DataPromise<IRootOverviewItemDto>
    setSearchResults: (searchResults: DataPromise<string[], undefined, GraphError>) => void
}

export type SearchRequestWrapper = { requests: SearchRequest[] }

/**
 * Compose a Search Query request body using the following documentation:
 * https://learn.microsoft.com/en-us/graph/search-concept-files#example-5-use-filters-in-search-queries
 *
 * @param query The search terms.
 * @param list The sharepoint list of the current environment.
 * @param fileReference The current File reference.
 */
const getSearchRequest = (query: string, list: List, fileReference: string): SearchRequestWrapper => ({
    requests: [
        {
            entityTypes: ['driveItem'],
            query: {
                queryString: `${query} AND path:"${list.webUrl}" AND ParentLink:"${fileReference}"`,
            },
        },
    ],
})

export default function DocumentSearchBox(props: Props): ReactElement {
    const sharePoint = useSharePoint()
    const [query, setQuery] = useState<string>()
    const [searchBoxValue, setSearchBoxValue] = useState<string>()
    const { t: translate } = useTranslation()

    const searchRequest = useMemo<SearchRequestWrapper>(
        () =>
            query && props.rootFolder.data?.url
                ? getSearchRequest(query, sharePoint.sharePoint.list, props.file.reference)
                : null,
        [query, props.rootFolder.data?.url],
    )

    const [searchResults] = useEntity<string[], IGraphArrayPayload<SearchResponse>, GraphError, SearchRequestWrapper>(
        data => ({ method: 'POST', url: '/search/query', data, baseURL: Destination.SharePoint }),
        searchRequest,
        result =>
            result.data.value.flatMap((value: SearchResponse) =>
                value.hitsContainers.flatMap(hitsContainer => hitsContainer?.hits?.map(hit => hit.hitId) || []),
            ),
    )

    useEffect(() => {
        setSearchBoxValue(query)
    }, [query])

    useEffect(() => {
        props.setSearchResults(searchResults)
    }, [searchResults])

    return (
        <SearchBox
            placeholder={translate('file.pivot.documents.search')}
            underlined
            autoFocus
            value={searchBoxValue || ''}
            onChange={(_, value) => setSearchBoxValue(value)}
            onSearch={value => setQuery(value !== '' ? value : undefined)}
            onClear={() => setQuery(undefined)}
        />
    )
}
